/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const N = globalThis, W = N.ShadowRoot && (N.ShadyCSS === void 0 || N.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, G = Symbol(), et = /* @__PURE__ */ new WeakMap();
let gt = class {
  constructor(t, e, r) {
    if (this._$cssResult$ = !0, r !== G) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (W && t === void 0) {
      const r = e !== void 0 && e.length === 1;
      r && (t = et.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), r && et.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const mt = (i) => new gt(typeof i == "string" ? i : i + "", void 0, G), _t = (i, ...t) => {
  const e = i.length === 1 ? i[0] : t.reduce((r, n, o) => r + ((s) => {
    if (s._$cssResult$ === !0) return s.cssText;
    if (typeof s == "number") return s;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + s + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(n) + i[o + 1], i[0]);
  return new gt(e, i, G);
}, $t = (i, t) => {
  if (W) i.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else for (const e of t) {
    const r = document.createElement("style"), n = N.litNonce;
    n !== void 0 && r.setAttribute("nonce", n), r.textContent = e.cssText, i.appendChild(r);
  }
}, rt = W ? (i) => i : (i) => i instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const r of t.cssRules) e += r.cssText;
  return mt(e);
})(i) : i;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: At, defineProperty: Et, getOwnPropertyDescriptor: St, getOwnPropertyNames: zt, getOwnPropertySymbols: Ct, getPrototypeOf: Ht } = Object, j = globalThis, it = j.trustedTypes, Mt = it ? it.emptyScript : "", Bt = j.reactiveElementPolyfillSupport, E = (i, t) => i, T = { toAttribute(i, t) {
  switch (t) {
    case Boolean:
      i = i ? Mt : null;
      break;
    case Object:
    case Array:
      i = i == null ? i : JSON.stringify(i);
  }
  return i;
}, fromAttribute(i, t) {
  let e = i;
  switch (t) {
    case Boolean:
      e = i !== null;
      break;
    case Number:
      e = i === null ? null : Number(i);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(i);
      } catch {
        e = null;
      }
  }
  return e;
} }, V = (i, t) => !At(i, t), nt = { attribute: !0, type: String, converter: T, reflect: !1, useDefault: !1, hasChanged: V };
Symbol.metadata ??= Symbol("metadata"), j.litPropertyMetadata ??= /* @__PURE__ */ new WeakMap();
let w = class extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ??= []).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = nt) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const r = Symbol(), n = this.getPropertyDescriptor(t, r, e);
      n !== void 0 && Et(this.prototype, t, n);
    }
  }
  static getPropertyDescriptor(t, e, r) {
    const { get: n, set: o } = St(this.prototype, t) ?? { get() {
      return this[e];
    }, set(s) {
      this[e] = s;
    } };
    return { get: n, set(s) {
      const d = n?.call(this);
      o?.call(this, s), this.requestUpdate(t, d, r);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? nt;
  }
  static _$Ei() {
    if (this.hasOwnProperty(E("elementProperties"))) return;
    const t = Ht(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(E("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(E("properties"))) {
      const e = this.properties, r = [...zt(e), ...Ct(e)];
      for (const n of r) this.createProperty(n, e[n]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [r, n] of e) this.elementProperties.set(r, n);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, r] of this.elementProperties) {
      const n = this._$Eu(e, r);
      n !== void 0 && this._$Eh.set(n, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const r = new Set(t.flat(1 / 0).reverse());
      for (const n of r) e.unshift(rt(n));
    } else t !== void 0 && e.push(rt(t));
    return e;
  }
  static _$Eu(t, e) {
    const r = e.attribute;
    return r === !1 ? void 0 : typeof r == "string" ? r : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), this.constructor.l?.forEach((t) => t(this));
  }
  addController(t) {
    (this._$EO ??= /* @__PURE__ */ new Set()).add(t), this.renderRoot !== void 0 && this.isConnected && t.hostConnected?.();
  }
  removeController(t) {
    this._$EO?.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const r of e.keys()) this.hasOwnProperty(r) && (t.set(r, this[r]), delete this[r]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return $t(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    this.renderRoot ??= this.createRenderRoot(), this.enableUpdating(!0), this._$EO?.forEach((t) => t.hostConnected?.());
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    this._$EO?.forEach((t) => t.hostDisconnected?.());
  }
  attributeChangedCallback(t, e, r) {
    this._$AK(t, r);
  }
  _$ET(t, e) {
    const r = this.constructor.elementProperties.get(t), n = this.constructor._$Eu(t, r);
    if (n !== void 0 && r.reflect === !0) {
      const o = (r.converter?.toAttribute !== void 0 ? r.converter : T).toAttribute(e, r.type);
      this._$Em = t, o == null ? this.removeAttribute(n) : this.setAttribute(n, o), this._$Em = null;
    }
  }
  _$AK(t, e) {
    const r = this.constructor, n = r._$Eh.get(t);
    if (n !== void 0 && this._$Em !== n) {
      const o = r.getPropertyOptions(n), s = typeof o.converter == "function" ? { fromAttribute: o.converter } : o.converter?.fromAttribute !== void 0 ? o.converter : T;
      this._$Em = n, this[n] = s.fromAttribute(e, o.type) ?? this._$Ej?.get(n) ?? null, this._$Em = null;
    }
  }
  requestUpdate(t, e, r) {
    if (t !== void 0) {
      const n = this.constructor, o = this[t];
      if (r ??= n.getPropertyOptions(t), !((r.hasChanged ?? V)(o, e) || r.useDefault && r.reflect && o === this._$Ej?.get(t) && !this.hasAttribute(n._$Eu(t, r)))) return;
      this.C(t, e, r);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: r, reflect: n, wrapped: o }, s) {
    r && !(this._$Ej ??= /* @__PURE__ */ new Map()).has(t) && (this._$Ej.set(t, s ?? e ?? this[t]), o !== !0 || s !== void 0) || (this._$AL.has(t) || (this.hasUpdated || r || (e = void 0), this._$AL.set(t, e)), n === !0 && this._$Em !== t && (this._$Eq ??= /* @__PURE__ */ new Set()).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ??= this.createRenderRoot(), this._$Ep) {
        for (const [n, o] of this._$Ep) this[n] = o;
        this._$Ep = void 0;
      }
      const r = this.constructor.elementProperties;
      if (r.size > 0) for (const [n, o] of r) {
        const { wrapped: s } = o, d = this[n];
        s !== !0 || this._$AL.has(n) || d === void 0 || this.C(n, void 0, o, d);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), this._$EO?.forEach((r) => r.hostUpdate?.()), this.update(e)) : this._$EM();
    } catch (r) {
      throw t = !1, this._$EM(), r;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    this._$EO?.forEach((e) => e.hostUpdated?.()), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq &&= this._$Eq.forEach((e) => this._$ET(e, this[e])), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
w.elementStyles = [], w.shadowRootOptions = { mode: "open" }, w[E("elementProperties")] = /* @__PURE__ */ new Map(), w[E("finalized")] = /* @__PURE__ */ new Map(), Bt?.({ ReactiveElement: w }), (j.reactiveElementVersions ??= []).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Y = globalThis, U = Y.trustedTypes, ot = U ? U.createPolicy("lit-html", { createHTML: (i) => i }) : void 0, pt = "$lit$", m = `lit$${Math.random().toFixed(9).slice(2)}$`, ft = "?" + m, Pt = `<${ft}>`, v = document, C = () => v.createComment(""), H = (i) => i === null || typeof i != "object" && typeof i != "function", K = Array.isArray, Ot = (i) => K(i) || typeof i?.[Symbol.iterator] == "function", I = `[ 	
\f\r]`, A = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, st = /-->/g, at = />/g, f = RegExp(`>|${I}(?:([^\\s"'>=/]+)(${I}*=${I}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), dt = /'/g, lt = /"/g, kt = /^(?:script|style|textarea|title)$/i, Rt = (i) => (t, ...e) => ({ _$litType$: i, strings: t, values: e }), Nt = Rt(1), p = Symbol.for("lit-noChange"), c = Symbol.for("lit-nothing"), ct = /* @__PURE__ */ new WeakMap(), k = v.createTreeWalker(v, 129);
function vt(i, t) {
  if (!K(i) || !i.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return ot !== void 0 ? ot.createHTML(t) : t;
}
const Tt = (i, t) => {
  const e = i.length - 1, r = [];
  let n, o = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", s = A;
  for (let d = 0; d < e; d++) {
    const a = i[d];
    let h, b, l = -1, u = 0;
    for (; u < a.length && (s.lastIndex = u, b = s.exec(a), b !== null); ) u = s.lastIndex, s === A ? b[1] === "!--" ? s = st : b[1] !== void 0 ? s = at : b[2] !== void 0 ? (kt.test(b[2]) && (n = RegExp("</" + b[2], "g")), s = f) : b[3] !== void 0 && (s = f) : s === f ? b[0] === ">" ? (s = n ?? A, l = -1) : b[1] === void 0 ? l = -2 : (l = s.lastIndex - b[2].length, h = b[1], s = b[3] === void 0 ? f : b[3] === '"' ? lt : dt) : s === lt || s === dt ? s = f : s === st || s === at ? s = A : (s = f, n = void 0);
    const g = s === f && i[d + 1].startsWith("/>") ? " " : "";
    o += s === A ? a + Pt : l >= 0 ? (r.push(h), a.slice(0, l) + pt + a.slice(l) + m + g) : a + m + (l === -2 ? d : g);
  }
  return [vt(i, o + (i[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), r];
};
class M {
  constructor({ strings: t, _$litType$: e }, r) {
    let n;
    this.parts = [];
    let o = 0, s = 0;
    const d = t.length - 1, a = this.parts, [h, b] = Tt(t, e);
    if (this.el = M.createElement(h, r), k.currentNode = this.el.content, e === 2 || e === 3) {
      const l = this.el.content.firstChild;
      l.replaceWith(...l.childNodes);
    }
    for (; (n = k.nextNode()) !== null && a.length < d; ) {
      if (n.nodeType === 1) {
        if (n.hasAttributes()) for (const l of n.getAttributeNames()) if (l.endsWith(pt)) {
          const u = b[s++], g = n.getAttribute(l).split(m), P = /([.?@])?(.*)/.exec(u);
          a.push({ type: 1, index: o, name: P[2], strings: g, ctor: P[1] === "." ? Lt : P[1] === "?" ? jt : P[1] === "@" ? Dt : D }), n.removeAttribute(l);
        } else l.startsWith(m) && (a.push({ type: 6, index: o }), n.removeAttribute(l));
        if (kt.test(n.tagName)) {
          const l = n.textContent.split(m), u = l.length - 1;
          if (u > 0) {
            n.textContent = U ? U.emptyScript : "";
            for (let g = 0; g < u; g++) n.append(l[g], C()), k.nextNode(), a.push({ type: 2, index: ++o });
            n.append(l[u], C());
          }
        }
      } else if (n.nodeType === 8) if (n.data === ft) a.push({ type: 2, index: o });
      else {
        let l = -1;
        for (; (l = n.data.indexOf(m, l + 1)) !== -1; ) a.push({ type: 7, index: o }), l += m.length - 1;
      }
      o++;
    }
  }
  static createElement(t, e) {
    const r = v.createElement("template");
    return r.innerHTML = t, r;
  }
}
function x(i, t, e = i, r) {
  if (t === p) return t;
  let n = r !== void 0 ? e._$Co?.[r] : e._$Cl;
  const o = H(t) ? void 0 : t._$litDirective$;
  return n?.constructor !== o && (n?._$AO?.(!1), o === void 0 ? n = void 0 : (n = new o(i), n._$AT(i, e, r)), r !== void 0 ? (e._$Co ??= [])[r] = n : e._$Cl = n), n !== void 0 && (t = x(i, n._$AS(i, t.values), n, r)), t;
}
class Ut {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: r } = this._$AD, n = (t?.creationScope ?? v).importNode(e, !0);
    k.currentNode = n;
    let o = k.nextNode(), s = 0, d = 0, a = r[0];
    for (; a !== void 0; ) {
      if (s === a.index) {
        let h;
        a.type === 2 ? h = new B(o, o.nextSibling, this, t) : a.type === 1 ? h = new a.ctor(o, a.name, a.strings, this, t) : a.type === 6 && (h = new It(o, this, t)), this._$AV.push(h), a = r[++d];
      }
      s !== a?.index && (o = k.nextNode(), s++);
    }
    return k.currentNode = v, n;
  }
  p(t) {
    let e = 0;
    for (const r of this._$AV) r !== void 0 && (r.strings !== void 0 ? (r._$AI(t, r, e), e += r.strings.length - 2) : r._$AI(t[e])), e++;
  }
}
class B {
  get _$AU() {
    return this._$AM?._$AU ?? this._$Cv;
  }
  constructor(t, e, r, n) {
    this.type = 2, this._$AH = c, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = r, this.options = n, this._$Cv = n?.isConnected ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && t?.nodeType === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = x(this, t, e), H(t) ? t === c || t == null || t === "" ? (this._$AH !== c && this._$AR(), this._$AH = c) : t !== this._$AH && t !== p && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : Ot(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== c && H(this._$AH) ? this._$AA.nextSibling.data = t : this.T(v.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    const { values: e, _$litType$: r } = t, n = typeof r == "number" ? this._$AC(t) : (r.el === void 0 && (r.el = M.createElement(vt(r.h, r.h[0]), this.options)), r);
    if (this._$AH?._$AD === n) this._$AH.p(e);
    else {
      const o = new Ut(n, this), s = o.u(this.options);
      o.p(e), this.T(s), this._$AH = o;
    }
  }
  _$AC(t) {
    let e = ct.get(t.strings);
    return e === void 0 && ct.set(t.strings, e = new M(t)), e;
  }
  k(t) {
    K(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let r, n = 0;
    for (const o of t) n === e.length ? e.push(r = new B(this.O(C()), this.O(C()), this, this.options)) : r = e[n], r._$AI(o), n++;
    n < e.length && (this._$AR(r && r._$AB.nextSibling, n), e.length = n);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    for (this._$AP?.(!1, !0, e); t && t !== this._$AB; ) {
      const r = t.nextSibling;
      t.remove(), t = r;
    }
  }
  setConnected(t) {
    this._$AM === void 0 && (this._$Cv = t, this._$AP?.(t));
  }
}
class D {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, r, n, o) {
    this.type = 1, this._$AH = c, this._$AN = void 0, this.element = t, this.name = e, this._$AM = n, this.options = o, r.length > 2 || r[0] !== "" || r[1] !== "" ? (this._$AH = Array(r.length - 1).fill(new String()), this.strings = r) : this._$AH = c;
  }
  _$AI(t, e = this, r, n) {
    const o = this.strings;
    let s = !1;
    if (o === void 0) t = x(this, t, e, 0), s = !H(t) || t !== this._$AH && t !== p, s && (this._$AH = t);
    else {
      const d = t;
      let a, h;
      for (t = o[0], a = 0; a < o.length - 1; a++) h = x(this, d[r + a], e, a), h === p && (h = this._$AH[a]), s ||= !H(h) || h !== this._$AH[a], h === c ? t = c : t !== c && (t += (h ?? "") + o[a + 1]), this._$AH[a] = h;
    }
    s && !n && this.j(t);
  }
  j(t) {
    t === c ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Lt extends D {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === c ? void 0 : t;
  }
}
class jt extends D {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== c);
  }
}
class Dt extends D {
  constructor(t, e, r, n, o) {
    super(t, e, r, n, o), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = x(this, t, e, 0) ?? c) === p) return;
    const r = this._$AH, n = t === c && r !== c || t.capture !== r.capture || t.once !== r.once || t.passive !== r.passive, o = t !== c && (r === c || n);
    n && this.element.removeEventListener(this.name, this, r), o && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    typeof this._$AH == "function" ? this._$AH.call(this.options?.host ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class It {
  constructor(t, e, r) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = r;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    x(this, t);
  }
}
const qt = Y.litHtmlPolyfillSupport;
qt?.(M, B), (Y.litHtmlVersions ??= []).push("3.3.0");
const Ft = (i, t, e) => {
  const r = e?.renderBefore ?? t;
  let n = r._$litPart$;
  if (n === void 0) {
    const o = e?.renderBefore ?? null;
    r._$litPart$ = n = new B(t.insertBefore(C(), o), o, void 0, e ?? {});
  }
  return n._$AI(i), n;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const J = globalThis;
let S = class extends w {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    const t = super.createRenderRoot();
    return this.renderOptions.renderBefore ??= t.firstChild, t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Ft(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    super.connectedCallback(), this._$Do?.setConnected(!0);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._$Do?.setConnected(!1);
  }
  render() {
    return p;
  }
};
S._$litElement$ = !0, S.finalized = !0, J.litElementHydrateSupport?.({ LitElement: S });
const Wt = J.litElementPolyfillSupport;
Wt?.({ LitElement: S });
(J.litElementVersions ??= []).push("4.2.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Gt = { attribute: !0, type: String, converter: T, reflect: !1, hasChanged: V }, Vt = (i = Gt, t, e) => {
  const { kind: r, metadata: n } = e;
  let o = globalThis.litPropertyMetadata.get(n);
  if (o === void 0 && globalThis.litPropertyMetadata.set(n, o = /* @__PURE__ */ new Map()), r === "setter" && ((i = Object.create(i)).wrapped = !0), o.set(e.name, i), r === "accessor") {
    const { name: s } = e;
    return { set(d) {
      const a = t.get.call(this);
      t.set.call(this, d), this.requestUpdate(s, a, i);
    }, init(d) {
      return d !== void 0 && this.C(s, void 0, i, d), d;
    } };
  }
  if (r === "setter") {
    const { name: s } = e;
    return function(d) {
      const a = this[s];
      t.call(this, d), this.requestUpdate(s, a, i);
    };
  }
  throw Error("Unsupported decorator location: " + r);
};
function y(i) {
  return (t, e) => typeof e == "object" ? Vt(i, t, e) : ((r, n, o) => {
    const s = n.hasOwnProperty(o);
    return n.constructor.createProperty(o, r), s ? Object.getOwnPropertyDescriptor(n, o) : void 0;
  })(i, t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function Yt(i) {
  return y({ ...i, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Q = { ATTRIBUTE: 1, CHILD: 2 }, Z = (i) => (...t) => ({ _$litDirective$: i, values: t });
class X {
  constructor(t) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(t, e, r) {
    this._$Ct = t, this._$AM = e, this._$Ci = r;
  }
  _$AS(t, e) {
    return this.update(t, e);
  }
  update(t, e) {
    return this.render(...e);
  }
}
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Kt = Z(class extends X {
  constructor(i) {
    if (super(i), i.type !== Q.ATTRIBUTE || i.name !== "class" || i.strings?.length > 2) throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(i) {
    return " " + Object.keys(i).filter((t) => i[t]).join(" ") + " ";
  }
  update(i, [t]) {
    if (this.st === void 0) {
      this.st = /* @__PURE__ */ new Set(), i.strings !== void 0 && (this.nt = new Set(i.strings.join(" ").split(/\s/).filter((r) => r !== "")));
      for (const r in t) t[r] && !this.nt?.has(r) && this.st.add(r);
      return this.render(t);
    }
    const e = i.element.classList;
    for (const r of this.st) r in t || (e.remove(r), this.st.delete(r));
    for (const r in t) {
      const n = !!t[r];
      n === this.st.has(r) || this.nt?.has(r) || (n ? (e.add(r), this.st.add(r)) : (e.remove(r), this.st.delete(r)));
    }
    return p;
  }
});
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Jt = (i) => i.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const z = (i, t) => {
  const e = i._$AN;
  if (e === void 0) return !1;
  for (const r of e) r._$AO?.(t, !1), z(r, t);
  return !0;
}, L = (i) => {
  let t, e;
  do {
    if ((t = i._$AM) === void 0) break;
    e = t._$AN, e.delete(i), i = t;
  } while (e?.size === 0);
}, wt = (i) => {
  for (let t; t = i._$AM; i = t) {
    let e = t._$AN;
    if (e === void 0) t._$AN = e = /* @__PURE__ */ new Set();
    else if (e.has(i)) break;
    e.add(i), Xt(t);
  }
};
function Qt(i) {
  this._$AN !== void 0 ? (L(this), this._$AM = i, wt(this)) : this._$AM = i;
}
function Zt(i, t = !1, e = 0) {
  const r = this._$AH, n = this._$AN;
  if (n !== void 0 && n.size !== 0) if (t) if (Array.isArray(r)) for (let o = e; o < r.length; o++) z(r[o], !1), L(r[o]);
  else r != null && (z(r, !1), L(r));
  else z(this, i);
}
const Xt = (i) => {
  i.type == Q.CHILD && (i._$AP ??= Zt, i._$AQ ??= Qt);
};
class te extends X {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(t, e, r) {
    super._$AT(t, e, r), wt(this), this.isConnected = t._$AU;
  }
  _$AO(t, e = !0) {
    t !== this.isConnected && (this.isConnected = t, t ? this.reconnected?.() : this.disconnected?.()), e && (z(this, t), L(this));
  }
  setValue(t) {
    if (Jt(this._$Ct)) this._$Ct._$AI(t, this);
    else {
      const e = [...this._$Ct._$AH];
      e[this._$Ci] = t, this._$Ct._$AI(e, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const O = () => new ee();
class ee {
}
const q = /* @__PURE__ */ new WeakMap(), R = Z(class extends te {
  render(i) {
    return c;
  }
  update(i, [t]) {
    const e = t !== this.G;
    return e && this.G !== void 0 && this.rt(void 0), (e || this.lt !== this.ct) && (this.G = t, this.ht = i.options?.host, this.rt(this.ct = i.element)), c;
  }
  rt(i) {
    if (this.isConnected || (i = void 0), typeof this.G == "function") {
      const t = this.ht ?? globalThis;
      let e = q.get(t);
      e === void 0 && (e = /* @__PURE__ */ new WeakMap(), q.set(t, e)), e.get(this.G) !== void 0 && this.G.call(this.ht, void 0), e.set(this.G, i), i !== void 0 && this.G.call(this.ht, i);
    } else this.G.value = i;
  }
  get lt() {
    return typeof this.G == "function" ? q.get(this.ht ?? globalThis)?.get(this.G) : this.G?.value;
  }
  disconnected() {
    this.lt === this.ct && this.rt(void 0);
  }
  reconnected() {
    this.rt(this.ct);
  }
});
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class F extends X {
  constructor(t) {
    if (super(t), this.it = c, t.type !== Q.CHILD) throw Error(this.constructor.directiveName + "() can only be used in child bindings");
  }
  render(t) {
    if (t === c || t == null) return this._t = void 0, this.it = t;
    if (t === p) return t;
    if (typeof t != "string") throw Error(this.constructor.directiveName + "() called with a non-string value");
    if (t === this.it) return this._t;
    this.it = t;
    const e = [t];
    return e.raw = e, this._t = { _$litType$: this.constructor.resultType, strings: e, values: [] };
  }
}
F.directiveName = "unsafeHTML", F.resultType = 1;
const re = Z(F);
function tt(i, t = !0) {
  return Array.isArray(i) ? i.map((e) => {
    if (t && Array.isArray(e))
      throw new Error(
        "The tuple syntax is not supported for `screens`."
      );
    if (typeof e == "string")
      return {
        name: e.toString(),
        not: !1,
        values: [{ min: e, max: void 0 }]
      };
    let [r, n] = e;
    return r = r.toString(), typeof n == "string" ? {
      name: r,
      not: !1,
      values: [{ min: n, max: void 0 }]
    } : Array.isArray(n) ? {
      name: r,
      not: !1,
      values: n.map((o) => ht(o))
    } : { name: r, not: !1, values: [ht(n)] };
  }) : tt(Object.entries(i ?? {}), !1);
}
function ht({
  "min-width": i,
  min: t = i,
  max: e,
  raw: r
} = {}) {
  return { min: t, max: e, raw: r };
}
const ie = { "2xs_and_up": "320px", "2xs_to_xs": { min: "320px", max: "359px" }, xs_and_up: "360px", xs_to_app: { min: "360px", max: "374px" }, app_and_up: "375px", app_to_s: { min: "375px", max: "559px" }, s_and_up: "560px", s_to_m: { min: "560px", max: "759px" }, m_and_up: "760px", m_to_l: { min: "760px", max: "959px" }, l_and_up: "960px", l_to_xl: { min: "960px", max: "1159px" }, xl_and_up: "1160px", xl_to_2xl: { min: "1160px", max: "1359px" }, "2xl_and_up": "1360px", "2xl_to_3xl": { min: "1360px", max: "1659px" }, "3xl_and_up": "1660px", "3xl_to_full-hd": { min: "1660px", max: "1919px" }, "full-hd_and_up": "1920px", "up_to_full-hd": { max: "1919px" }, up_to_3xl: { max: "1659px" }, up_to_2xl: { max: "1359px" }, up_to_xl: { max: "1159px" }, up_to_l: { max: "959px" }, up_to_m: { max: "759px" }, up_to_s: { max: "559px" }, up_to_app: { max: "374px" }, up_to_xs: { max: "359px" }, up_to_2xs: { max: "319px" } }, xt = {
  screens: ie
};
function yt(i) {
  return i = Array.isArray(i) ? i : [i], i.map((t) => {
    let e = t.values.map((r) => r.raw !== void 0 ? r.raw : [
      r.min && `min-width: ${r.min}`,
      r.max && `max-width: ${r.max}`
    ].filter(Boolean).join(" and "));
    return t.not ? `not all and ${e}` : e;
  }).join(", ");
}
function bt() {
  const i = tt(xt.screens), t = "m_and_up", e = i.find((r) => r.name === t);
  return e || console.log(`No \`${t}\` screen found.`), yt(e);
}
function ut() {
  const i = tt(xt.screens), t = "up_to_m", e = i.find((r) => r.name === t);
  return e || console.log(`No \`${t}\` screen found.`), yt(e);
}
const ne = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.3627 5.63727 5.63733 18.3627m12.72537.0L5.63733 5.63727" stroke="currentColor" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="square"/></svg>', oe = 'html{font-size:6px}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:500;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Medium.woff2) format("woff2")}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:400;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Regular.woff2) format("woff2")}html,:root,:host{--bdk-alabaster: rgba(250,248,247,1);--bdk-ash: rgba(108,103,101,1);--bdk-beyond-bronze: rgba(157,83,39,1);--bdk-beyond-gold: rgba(158,91,5,1);--bdk-beyond-platin: rgba(132,93,104,1);--bdk-breuninger-orange: rgba(243,138,1,1);--bdk-breuninger-red: rgba(186,12,47,1);--bdk-bronze-background: rgba(248,236,232,1);--bdk-bronze-gradient: rgba(0,0,0,1);--bdk-coal-100: rgba(26,26,26,1);--bdk-concrete: rgba(244,242,242,1);--bdk-error: rgba(178,12,22,1);--bdk-error-background: rgba(255,237,244,1);--bdk-eukalyptus: rgba(201,229,216,1);--bdk-gold-gradient: rgba(0,0,0,1);--bdk-home-and-living: rgba(186,180,180,1);--bdk-marketplace: rgba(16,53,185,1);--bdk-orange-background: rgba(255,244,235,1);--bdk-pebble: rgba(224,223,222,1);--bdk-platin-gradient: rgba(0,0,0,1);--bdk-private-luxury-sale: rgba(207,199,192,1);--bdk-red-hover-light: rgba(203,13,51,1);--bdk-sale: rgba(255,237,0,1);--bdk-silber-gradient: rgba(0,0,0,1);--bdk-smoke: rgba(146,141,139,1);--bdk-snow-100: rgba(255,255,255,1);--bdk-success: rgba(19,98,51,1);--bdk-success-background: rgba(239,253,229,1);--bdk-space-0: 0rem;--bdk-space-0-5: .333rem;--bdk-space-1: .667rem;--bdk-space-10: 6.667rem;--bdk-space-11: 7.333rem;--bdk-space-12: 8rem;--bdk-space-13: 8.667rem;--bdk-space-14: 9.333rem;--bdk-space-15: 10rem;--bdk-space-16: 10.667rem;--bdk-space-17: 11.333rem;--bdk-space-18: 12rem;--bdk-space-19: 12.667rem;--bdk-space-2: 1.333rem;--bdk-space-2-5: 1.667rem;--bdk-space-20: 13.333rem;--bdk-space-21: 14rem;--bdk-space-22: 14.667rem;--bdk-space-23: 15.333rem;--bdk-space-24: 16rem;--bdk-space-25: 16.667rem;--bdk-space-26: 17.333rem;--bdk-space-27: 18rem;--bdk-space-28: 18.667rem;--bdk-space-29: 19.333rem;--bdk-space-3: 2rem;--bdk-space-30: 20rem;--bdk-space-31: 20.667rem;--bdk-space-32: 21.333rem;--bdk-space-33: 22rem;--bdk-space-34: 22.667rem;--bdk-space-35: 23.333rem;--bdk-space-4: 2.667rem;--bdk-space-5: 3.333rem;--bdk-space-6: 4rem;--bdk-space-7: 4.667rem;--bdk-space-8: 5.333rem;--bdk-space-9: 6rem;--bdk-radius-0: 0rem;--bdk-radius-1: .667rem;--bdk-radius-1-5: 1rem;--bdk-radius-10: 6.667rem;--bdk-radius-11: 7.333rem;--bdk-radius-12: 8rem;--bdk-radius-2: 1.333rem;--bdk-radius-3: 2rem;--bdk-radius-4: 2.667rem;--bdk-radius-5: 3.333rem;--bdk-radius-6: 4rem;--bdk-radius-7: 4.667rem;--bdk-radius-8: 5.333rem;--bdk-radius-9: 6rem;--bdk-radius-pill: 166.5rem;--bdk-button-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-button-l-font-size: 2.667rem;--bdk-button-l-font-weight: 500;--bdk-button-l-letter-spacing: .05rem;--bdk-button-l-line-height: 4rem;--bdk-button-l-text-decoration: none;--bdk-button-l-text-transform: uppercase;--bdk-button-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-button-m-font-size: 2.333rem;--bdk-button-m-font-weight: 500;--bdk-button-m-letter-spacing: .033rem;--bdk-button-m-line-height: 3.667rem;--bdk-button-m-text-decoration: none;--bdk-button-m-text-transform: uppercase;--bdk-button-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-button-s-font-size: 2rem;--bdk-button-s-font-weight: 500;--bdk-button-s-letter-spacing: .033rem;--bdk-button-s-line-height: 3rem;--bdk-button-s-text-decoration: none;--bdk-button-s-text-transform: uppercase;--bdk-heading-2xl-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-2xl-font-size: 7rem;--bdk-heading-2xl-font-weight: 500;--bdk-heading-2xl-letter-spacing: .267rem;--bdk-heading-2xl-line-height: 10.667rem;--bdk-heading-2xl-text-decoration: none;--bdk-heading-2xl-text-transform: uppercase;--bdk-heading-2xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-2xs-font-size: 1.667rem;--bdk-heading-2xs-font-weight: 500;--bdk-heading-2xs-letter-spacing: .017rem;--bdk-heading-2xs-line-height: 2.667rem;--bdk-heading-2xs-text-decoration: none;--bdk-heading-2xs-text-transform: uppercase;--bdk-heading-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-l-font-size: 4rem;--bdk-heading-l-font-weight: 500;--bdk-heading-l-letter-spacing: .117rem;--bdk-heading-l-line-height: 6rem;--bdk-heading-l-text-decoration: none;--bdk-heading-l-text-transform: uppercase;--bdk-heading-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-m-font-size: 3.333rem;--bdk-heading-m-font-weight: 500;--bdk-heading-m-letter-spacing: .083rem;--bdk-heading-m-line-height: 5rem;--bdk-heading-m-text-decoration: none;--bdk-heading-m-text-transform: uppercase;--bdk-heading-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-s-font-size: 2.667rem;--bdk-heading-s-font-weight: 500;--bdk-heading-s-letter-spacing: .05rem;--bdk-heading-s-line-height: 4rem;--bdk-heading-s-text-decoration: none;--bdk-heading-s-text-transform: uppercase;--bdk-heading-xl-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-xl-font-size: 5.333rem;--bdk-heading-xl-font-weight: 500;--bdk-heading-xl-letter-spacing: .2rem;--bdk-heading-xl-line-height: 8rem;--bdk-heading-xl-text-decoration: none;--bdk-heading-xl-text-transform: uppercase;--bdk-heading-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-xs-font-size: 2.333rem;--bdk-heading-xs-font-weight: 500;--bdk-heading-xs-letter-spacing: .033rem;--bdk-heading-xs-line-height: 3.667rem;--bdk-heading-xs-text-decoration: none;--bdk-heading-xs-text-transform: uppercase;--bdk-list-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-list-m-font-size: 2.667rem;--bdk-list-m-font-weight: 400;--bdk-list-m-letter-spacing: .067rem;--bdk-list-m-line-height: 4rem;--bdk-list-m-text-decoration: none;--bdk-list-m-text-transform: inherit;--bdk-strong-2xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-2xs-font-size: 1.667rem;--bdk-strong-2xs-font-weight: 500;--bdk-strong-2xs-letter-spacing: .017rem;--bdk-strong-2xs-line-height: 2.667rem;--bdk-strong-2xs-text-decoration: none;--bdk-strong-2xs-text-transform: inherit;--bdk-strong-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-l-font-size: 3.333rem;--bdk-strong-l-font-weight: 500;--bdk-strong-l-letter-spacing: .1rem;--bdk-strong-l-line-height: 5rem;--bdk-strong-l-text-decoration: none;--bdk-strong-l-text-transform: inherit;--bdk-strong-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-m-font-size: 2.667rem;--bdk-strong-m-font-weight: 500;--bdk-strong-m-letter-spacing: .067rem;--bdk-strong-m-line-height: 4rem;--bdk-strong-m-text-decoration: none;--bdk-strong-m-text-transform: inherit;--bdk-strong-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-s-font-size: 2.333rem;--bdk-strong-s-font-weight: 500;--bdk-strong-s-letter-spacing: .05rem;--bdk-strong-s-line-height: 3.667rem;--bdk-strong-s-text-decoration: none;--bdk-strong-s-text-transform: inherit;--bdk-strong-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-xs-font-size: 2rem;--bdk-strong-xs-font-weight: 500;--bdk-strong-xs-letter-spacing: .033rem;--bdk-strong-xs-line-height: 3rem;--bdk-strong-xs-text-decoration: none;--bdk-strong-xs-text-transform: inherit;--bdk-text-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-l-font-size: 3.333rem;--bdk-text-l-font-weight: 400;--bdk-text-l-letter-spacing: .1rem;--bdk-text-l-line-height: 5rem;--bdk-text-l-text-decoration: none;--bdk-text-l-text-transform: inherit;--bdk-text-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-m-font-size: 2.667rem;--bdk-text-m-font-weight: 400;--bdk-text-m-letter-spacing: .067rem;--bdk-text-m-line-height: 4rem;--bdk-text-m-text-decoration: none;--bdk-text-m-text-transform: inherit;--bdk-text-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-s-font-size: 2.333rem;--bdk-text-s-font-weight: 400;--bdk-text-s-letter-spacing: .05rem;--bdk-text-s-line-height: 3.667rem;--bdk-text-s-text-decoration: none;--bdk-text-s-text-transform: inherit;--bdk-text-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-xs-font-size: 2rem;--bdk-text-xs-font-weight: 400;--bdk-text-xs-letter-spacing: .033rem;--bdk-text-xs-line-height: 3rem;--bdk-text-xs-text-decoration: none;--bdk-text-xs-text-transform: inherit;--bdk-underline-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-m-font-size: 2.667rem;--bdk-underline-m-font-weight: 400;--bdk-underline-m-letter-spacing: .067rem;--bdk-underline-m-line-height: 4rem;--bdk-underline-m-text-decoration: underline;--bdk-underline-m-text-transform: inherit;--bdk-underline-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-s-font-size: 2.333rem;--bdk-underline-s-font-weight: 400;--bdk-underline-s-letter-spacing: .05rem;--bdk-underline-s-line-height: 3.667rem;--bdk-underline-s-text-decoration: underline;--bdk-underline-s-text-transform: inherit;--bdk-underline-strong-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-strong-m-font-size: 2.667rem;--bdk-underline-strong-m-font-weight: 500;--bdk-underline-strong-m-letter-spacing: .067rem;--bdk-underline-strong-m-line-height: 4rem;--bdk-underline-strong-m-text-decoration: underline;--bdk-underline-strong-m-text-transform: inherit;--bdk-underline-strong-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-strong-s-font-size: 2.333rem;--bdk-underline-strong-s-font-weight: 500;--bdk-underline-strong-s-letter-spacing: .05rem;--bdk-underline-strong-s-line-height: 3.667rem;--bdk-underline-strong-s-text-decoration: underline;--bdk-underline-strong-s-text-transform: inherit;--bdk-underline-strong-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-strong-xs-font-size: 2rem;--bdk-underline-strong-xs-font-weight: 500;--bdk-underline-strong-xs-letter-spacing: .033rem;--bdk-underline-strong-xs-line-height: 3rem;--bdk-underline-strong-xs-text-decoration: underline;--bdk-underline-strong-xs-text-transform: inherit;--bdk-underline-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-xs-font-size: 2rem;--bdk-underline-xs-font-weight: 400;--bdk-underline-xs-letter-spacing: .033rem;--bdk-underline-xs-line-height: 3rem;--bdk-underline-xs-text-decoration: underline;--bdk-underline-xs-text-transform: inherit;--bdk-breakpoint-2xl: 1360px;--bdk-breakpoint-2xs: 320px;--bdk-breakpoint-3xl: 1660px;--bdk-breakpoint-app: 375px;--bdk-breakpoint-full-hd: 1920px;--bdk-breakpoint-l: 960px;--bdk-breakpoint-m: 760px;--bdk-breakpoint-s: 560px;--bdk-breakpoint-xl: 1160px;--bdk-breakpoint-xs: 360px;--bdk-elevation-above-header: 45;--bdk-elevation-above-menu: 55;--bdk-elevation-above-overlay: 85;--bdk-elevation-below-header: 35;--bdk-elevation-below-menu: 45;--bdk-elevation-below-overlay: 55;--bdk-elevation-header: 40;--bdk-elevation-layer: 90;--bdk-elevation-menu: 50;--bdk-elevation-offcanvas: 100;--bdk-elevation-overlay: 80;--bdk-shadow-elevation--1: 0rem -.833rem .833rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation--2: 0rem -1.667rem 2.5rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation--3: 0rem -4.167rem 5rem 0rem rgba(26,26,26,.04);--bdk-shadow-elevation--4: 0rem -6.667rem 6.667rem 0rem rgba(26,26,26,.04);--bdk-shadow-elevation-1: 0rem .833rem .833rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation-2: 0rem 1.667rem 2.5rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation-3: 0rem 4.167rem 5rem 0rem rgba(26,26,26,.04);--bdk-shadow-elevation-4: 0rem 6.667rem 6.667rem 0rem rgba(26,26,26,.04)}*,:before,:after{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }::backdrop{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }*,:before,:after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}:before,:after{--tw-content: ""}html,:host{line-height:1.5;-webkit-text-size-adjust:100%;-moz-tab-size:4;-o-tab-size:4;tab-size:4;font-family:Eduard Breuninger,ui-sans-serif,system-ui,sans-serif,"Apple Color Emoji","Segoe UI Emoji",Segoe UI Symbol,"Noto Color Emoji";font-feature-settings:normal;font-variation-settings:normal;-webkit-tap-highlight-color:transparent}body{margin:0;line-height:inherit}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;text-decoration:inherit}b,strong{font-weight:bolder}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-feature-settings:normal;font-variation-settings:normal;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-feature-settings:inherit;font-variation-settings:inherit;font-size:100%;font-weight:inherit;line-height:inherit;letter-spacing:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,input:where([type=button]),input:where([type=reset]),input:where([type=submit]){-webkit-appearance:button;background-color:transparent;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{list-style:none;margin:0;padding:0}dialog{padding:0}textarea{resize:vertical}input::-moz-placeholder,textarea::-moz-placeholder{opacity:1;color:#9ca3af}input::placeholder,textarea::placeholder{opacity:1;color:#9ca3af}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}[hidden]:where(:not([hidden=until-found])){display:none}.bdk-tw-heading-m{font-family:Eduard Breuninger;font-size:3.333rem;font-weight:500;line-height:5rem;letter-spacing:.083rem;text-transform:uppercase}.bdk-tw-button-icon{--bdk-button-icon-size-extra-extra-small: var(--bdk-space-6, 4rem);--bdk-button-icon-size-extra-small: var(--bdk-space-8, 5.333rem);--bdk-button-icon-size-small: var(--bdk-space-11, 7.333rem);--bdk-button-icon-size-medium: var(--bdk-space-14, 9.333rem);--bdk-button-icon-size-large: var(--bdk-space-16, 10.667rem);--bdk-button-icon-radius-pill: var(--bdk-radius-pill, 166.5rem);--bdk-button-icon-radius-rounded: var(--bdk-radius-2, 1.333rem);--bdk-button-icon-radius: var(--bdk-button-icon-radius-rounded);display:inline-flex;width:var(--bdk-button-icon-size);aspect-ratio:1;border-width:1px;border-style:solid;align-items:center;justify-content:center;text-align:center;cursor:pointer;border-radius:var(--bdk-button-icon-radius);transition:background-color var(--bdk-transition-duration-default, .2s) ease,color var(--bdk-transition-duration-default, .2s) ease,border-color var(--bdk-transition-duration-default, .2s) ease}@media (prefers-reduced-motion: reduce){.bdk-tw-button-icon{transition:none}}.bdk-tw-button-icon:not(:disabled):focus-visible{outline:2px solid var(--bdk-focus-color);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color)}.bdk-tw-button-icon:not(:disabled)[inverted]:focus-visible{outline:2px solid var(--bdk-focus-color-inverted);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color-inverted)}.bdk-tw-button-icon[size=extra-extra-small]{--bdk-button-icon-size: var(--bdk-button-icon-size-extra-extra-small);font-family:var(--bdk-button-s-font-family, "Eduard Breuninger", Helvetica, Arial, sans-serif);font-size:var(--bdk-button-s-font-size, 2rem);font-weight:var(--bdk-button-s-font-weight, 500);letter-spacing:var(--bdk-button-s-letter-spacing, .033rem);line-height:var(--bdk-button-s-line-height, 3rem);-webkit-text-decoration:var(--bdk-button-s-text-decoration, none);text-decoration:var(--bdk-button-s-text-decoration, none);text-transform:var(--bdk-button-s-text-transform, uppercase)}.bdk-tw-button-icon[size=extra-small]{--bdk-button-icon-size: var(--bdk-button-icon-size-extra-small);font-family:var(--bdk-button-s-font-family, "Eduard Breuninger", Helvetica, Arial, sans-serif);font-size:var(--bdk-button-s-font-size, 2rem);font-weight:var(--bdk-button-s-font-weight, 500);letter-spacing:var(--bdk-button-s-letter-spacing, .033rem);line-height:var(--bdk-button-s-line-height, 3rem);-webkit-text-decoration:var(--bdk-button-s-text-decoration, none);text-decoration:var(--bdk-button-s-text-decoration, none);text-transform:var(--bdk-button-s-text-transform, uppercase)}.bdk-tw-button-icon[size=small]{--bdk-button-icon-size: var(--bdk-button-icon-size-small);font-family:var(--bdk-button-s-font-family, "Eduard Breuninger", Helvetica, Arial, sans-serif);font-size:var(--bdk-button-s-font-size, 2rem);font-weight:var(--bdk-button-s-font-weight, 500);letter-spacing:var(--bdk-button-s-letter-spacing, .033rem);line-height:var(--bdk-button-s-line-height, 3rem);-webkit-text-decoration:var(--bdk-button-s-text-decoration, none);text-decoration:var(--bdk-button-s-text-decoration, none);text-transform:var(--bdk-button-s-text-transform, uppercase)}.bdk-tw-button-icon[size=medium]{--bdk-button-icon-size: var(--bdk-button-icon-size-medium);font-family:var(--bdk-button-m-font-family, "Eduard Breuninger", Helvetica, Arial, sans-serif);font-size:var(--bdk-button-m-font-size, 2.333rem);font-weight:var(--bdk-button-m-font-weight, 500);letter-spacing:var(--bdk-button-m-letter-spacing, .033rem);line-height:var(--bdk-button-m-line-height, 3.667rem);-webkit-text-decoration:var(--bdk-button-m-text-decoration, none);text-decoration:var(--bdk-button-m-text-decoration, none);text-transform:var(--bdk-button-m-text-transform, uppercase)}.bdk-tw-button-icon[size=large]{--bdk-button-icon-size: var(--bdk-button-icon-size-large);font-family:var(--bdk-button-l-font-family, "Eduard Breuninger", Helvetica, Arial, sans-serif);font-size:var(--bdk-button-l-font-size, 2.667rem);font-weight:var(--bdk-button-l-font-weight, 500);letter-spacing:var(--bdk-button-l-letter-spacing, .05rem);line-height:var(--bdk-button-l-line-height, 4rem);-webkit-text-decoration:var(--bdk-button-l-text-decoration, none);text-decoration:var(--bdk-button-l-text-decoration, none);text-transform:var(--bdk-button-l-text-transform, uppercase)}.bdk-tw-button-icon[circle]{--bdk-button-icon-radius: var(--bdk-button-icon-radius-pill)}.bdk-tw-button-icon[variant=primary]{background-color:var(--bdk-coal-100, rgb(26, 26, 26));border-color:var(--bdk-coal-100, rgb(26, 26, 26));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=primary]:hover{background-color:var(--bdk-red-hover-light, rgb(203, 13, 51));border-color:var(--bdk-red-hover-light, rgb(203, 13, 51));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=primary]:disabled{cursor:auto;background-color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 25%,transparent);border-color:transparent;color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 50%,transparent)}.bdk-tw-button-icon[variant=primary][inverted]{background-color:var(--bdk-snow-100, rgb(255, 255, 255));border-color:var(--bdk-snow-100, rgb(255, 255, 255));color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=primary][inverted]:hover{background-color:var(--bdk-red-hover-light, rgb(203, 13, 51));border-color:var(--bdk-red-hover-light, rgb(203, 13, 51));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=primary][inverted]:disabled{color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 25%,transparent);background-color:#ffffff40;border-color:transparent}.bdk-tw-button-icon[variant=highlight]{background-color:var(--bdk-red-hover-light, rgb(203, 13, 51));border-color:var(--bdk-red-hover-light, rgb(203, 13, 51));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=highlight]:hover{background-color:var(--bdk-coal-100, rgb(26, 26, 26));border-color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=highlight]:disabled{cursor:auto;background-color:var(--bdk-red-hover-light, rgb(203, 13, 51));border-color:var(--bdk-red-hover-light, rgb(203, 13, 51));color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 50%,transparent)}.bdk-tw-button-icon[variant=highlight][inverted]:hover{background-color:var(--bdk-snow-100, rgb(255, 255, 255));border-color:var(--bdk-snow-100, rgb(255, 255, 255));color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=highlight][inverted]:disabled{cursor:auto;background-color:var(--bdk-red-hover-light, rgb(203, 13, 51));border-color:var(--bdk-red-hover-light, rgb(203, 13, 51));color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 50%,transparent)}.bdk-tw-button-icon[variant=outline]{background-color:transparent;border-color:var(--bdk-coal-100, rgb(26, 26, 26));color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=outline]:hover{background-color:var(--bdk-coal-100, rgb(26, 26, 26));border-color:var(--bdk-coal-100, rgb(26, 26, 26));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=outline]:disabled{cursor:auto;background-color:transparent;border-color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 25%,transparent);color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 25%,transparent)}.bdk-tw-button-icon[variant=outline][inverted]{background-color:transparent;border-color:var(--bdk-snow-100, rgb(255, 255, 255));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=outline][inverted]:hover{background-color:var(--bdk-snow-100, rgb(255, 255, 255));border-color:var(--bdk-snow-100, rgb(255, 255, 255));color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=outline][inverted]:disabled{cursor:auto;background-color:transparent;border-color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 25%,transparent);color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 25%,transparent)}.bdk-tw-button-icon[variant=overlay-neutral]{background-color:var(--bdk-concrete, rgb(244, 242, 242));border-color:var(--bdk-concrete, rgb(244, 242, 242));color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=overlay-neutral]:hover{background-color:var(--bdk-coal-100, rgb(26, 26, 26));border-color:var(--bdk-coal-100, rgb(26, 26, 26));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=overlay-neutral]:disabled{cursor:auto;background-color:var(--bdk-alabaster, rgb(250, 248, 247));border-color:var(--bdk-alabaster, rgb(250, 248, 247));color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 25%,transparent)}.bdk-tw-button-icon[variant=overlay-light]{background-color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 50%,transparent);border-color:transparent;color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=overlay-light]:hover{background-color:var(--bdk-coal-100, rgb(26, 26, 26));border-color:var(--bdk-coal-100, rgb(26, 26, 26));color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=overlay-light]:disabled{cursor:auto;background-color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 50%,transparent);border-color:transparent;color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 25%,transparent)}.bdk-tw-button-icon[variant=overlay-dark]{background-color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 50%,transparent);border-color:transparent;color:var(--bdk-snow-100, rgb(255, 255, 255))}.bdk-tw-button-icon[variant=overlay-dark]:hover{background-color:var(--bdk-snow-100, rgb(255, 255, 255));color:var(--bdk-coal-100, rgb(26, 26, 26))}.bdk-tw-button-icon[variant=overlay-dark]:disabled{background-color:color-mix(in srgb,var(--bdk-coal-100, rgb(26, 26, 26)) 25%,transparent);color:color-mix(in srgb,var(--bdk-snow-100, rgb(255, 255, 255)) 50%,transparent)}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0}.static{position:static}.fixed{position:fixed}.relative{position:relative}.inset-0{inset:0rem}.z-10{z-index:10}.z-20{z-index:20}.m-1{margin:.667rem}.mb-2{margin-bottom:1.333rem}.block{display:block}.inline{display:inline}.flex{display:flex}.h-\\[48px\\]{height:48px}.h-\\[800px\\]{height:800px}.h-full{height:100%}.w-\\[48px\\]{width:48px}.w-full{width:100%}.transform{transform:translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))}.cursor-default{cursor:default}.resize{resize:both}.flex-col{flex-direction:column}.flex-nowrap{flex-wrap:nowrap}.items-start{align-items:flex-start}.items-center{align-items:center}.justify-center{justify-content:center}.justify-between{justify-content:space-between}.gap-4{gap:2.667rem}.overflow-auto{overflow:auto}.overflow-hidden{overflow:hidden}.rounded{border-radius:.25rem}.rounded-2{border-radius:1.333rem}.bg-black{--tw-bg-opacity: 1;background-color:rgb(0 0 0 / var(--tw-bg-opacity, 1))}.bg-white{--tw-bg-opacity: 1;background-color:rgb(255 255 255 / var(--tw-bg-opacity, 1))}.bg-opacity-50{--tw-bg-opacity: .5}.p-0{padding:0rem}.px-3{padding-left:2rem;padding-right:2rem}.pb-10{padding-bottom:6.667rem}.pb-4{padding-bottom:2.667rem}.pt-3{padding-top:2rem}.text-left{text-align:left}.filter{filter:var(--tw-blur) var(--tw-brightness) var(--tw-contrast) var(--tw-grayscale) var(--tw-hue-rotate) var(--tw-invert) var(--tw-saturate) var(--tw-sepia) var(--tw-drop-shadow)}.ease-in{transition-timing-function:cubic-bezier(.4,0,1,1)}.ease-out{transition-timing-function:cubic-bezier(0,0,.2,1)}@keyframes backdrop-fade{0%{background-color:transparent}to{background-color:color-mix(in srgb,var(--bdk-coal-100) 50%,transparent)}}dialog{--bdk-modal-default-max-width: 688px;--bdk-modal-large-max-width: 960px;--bdk-modal-full-screen-max-width: 100vw;--bdk-modal-margin-large: 48px;--bdk-modal-mobile-margin: 90px;--bdk-modal-desktop-margin: 120px;background-color:var(--bdk-snow-100);margin-top:var(--bdk-modal-mobile-margin);inset:auto auto 0;inset-inline-start:0;border-top-left-radius:var(--bdk-radius-3);border-top-right-radius:var(--bdk-radius-3);border-bottom-left-radius:0;border-bottom-right-radius:0}@media screen and (min-width: 760px){dialog{border-bottom-left-radius:var(--bdk-radius-3);border-bottom-right-radius:var(--bdk-radius-3)}}dialog::backdrop{position:fixed;top:0;left:0;width:100%;height:100%;animation:backdrop-fade .2s ease-out backwards;animation-direction:reverse}@media (prefers-reduced-motion: reduce){{animation:none;background-color:transparent}}dialog[open]::backdrop{animation:backdrop-fade .2s ease-in forwards}@media (prefers-reduced-motion: reduce){{animation:none;background-color:var(--bdk-backdrop-layer, rgba(0, 0, 0, .5))}}dialog.width-default{width:100%;max-width:100%}@media screen and (min-width: 760px){dialog.width-default{max-width:var(--bdk-modal-default-max-width)}}dialog.width-large{width:100%;max-width:100%}@media screen and (min-width: 760px){dialog.width-large{max-width:var(--bdk-modal-large-max-width)}}dialog.full-screen{border-radius:0}@media screen and (min-width: 760px){dialog.full-screen{border-radius:var(--bdk-radius-3)}dialog:not(.height-large){margin-top:var(--bdk-modal-desktop-margin);margin-bottom:var(--bdk-modal-desktop-margin);inset:0}dialog.height-large{margin-top:var(--bdk-modal-margin-large);margin-bottom:var(--bdk-modal-margin-large);inset:0}dialog.full-screen{inset:0}}@media screen and (max-device-width: 1024px) and (orientation: portrait) and (-webkit-min-device-pixel-ratio: 1){dialog{margin-top:var(--bdk-modal-mobile-margin)}}@media screen and (max-device-width: 1024px) and (orientation: landscape) and (-webkit-min-device-pixel-ratio: 1){dialog{margin-top:var(--bdk-modal-mobile-margin)}}@media screen and (max-width: 759px){dialog.full-screen{margin:0;width:100%;max-width:var(--bdk-modal-full-screen-max-width);height:100dvh;max-height:100dvh;inset:0}}@media screen and (min-width: 760px){dialog{margin-top:var(--bdk-modal-desktop-margin);margin-bottom:var(--bdk-modal-desktop-margin)}}.hover\\:text-red-hover-light:hover{color:#cb0d33}.focus\\:outline-none:focus{outline:2px solid transparent;outline-offset:2px}.focus\\:ring-2:focus{--tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);--tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);box-shadow:var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow, 0 0 #0000)}@media (min-width: 760px){.m_and_up\\:flex{display:flex}.m_and_up\\:flex-col{flex-direction:column}.m_and_up\\:items-start{align-items:flex-start}.m_and_up\\:px-10{padding-left:6.667rem;padding-right:6.667rem}.m_and_up\\:pb-10{padding-bottom:6.667rem}.m_and_up\\:pb-15{padding-bottom:10rem}}';
var se = Object.defineProperty, _ = (i, t, e, r) => {
  for (var n = void 0, o = i.length - 1, s; o >= 0; o--)
    (s = i[o]) && (n = s(t, e, n) || n);
  return n && se(t, e, n), n;
};
class $ extends S {
  constructor() {
    super(...arguments), this.modalRef = O(), this.modalBodyRef = O(), this.modalHeaderRef = O(), this.modalFooterRef = O(), this.observer = null, this.nodeObservers = /* @__PURE__ */ new WeakMap(), this.observedNodes = [], this.prefix = "", this.fullScreen = !1, this.size = "default", this.open = !1, this.ariaLabelClose = "", this.needsLargerHeight = !1, this.handleSlotChange = (t) => {
      const r = t.target.assignedNodes();
      this.observedNodes.forEach((n) => {
        r.includes(n) || this.unobserveNode(n);
      }), r.forEach((n) => {
        this.observedNodes.includes(n) || this.observeNode(n);
      }), this.observedNodes = r;
    }, this.handleWrapperClick = (t) => {
      t.stopPropagation();
    }, this.handleOutsideClick = (t) => {
      this.hide();
    }, this.updateModalBodyHeight = () => {
      this.modalHeaderRef.value && this.modalBodyRef.value && (this.modalBodyRef.value.style.maxHeight = this.calculateMaxHeight());
    };
  }
  connectedCallback() {
    super.connectedCallback(), window.addEventListener("resize", this.updateModalBodyHeight), this.setupMutationObserver(), this.onClose = this.onClose.bind(this);
  }
  disconnectedCallback() {
    document.body.inert = !1, window.removeEventListener("resize", this.updateModalBodyHeight), document.removeEventListener("click", this.handleOutsideClick), this.disconnectMutationObserver(), this.modalRef.value?.removeEventListener("close", this.onClose), this.showModalAnimation?.cancel(), this.hideModalAnimation?.cancel(), super.disconnectedCallback();
  }
  firstUpdated() {
    const t = this.modalRef.value;
    if (!t)
      return;
    const e = new KeyframeEffect(
      t,
      [{ transform: "translateY(4rem)" }, { transform: "translateY(0)" }],
      { duration: 300, easing: "ease-out" }
    );
    this.showModalAnimation = new Animation(e, document.timeline);
    const r = new KeyframeEffect(
      t,
      [{ transform: "translateY(0)" }, { transform: "translateY(6rem)" }],
      { duration: 200, easing: "ease-in" }
    );
    this.modalRef.value.addEventListener("close", this.onClose), this.hideModalAnimation = new Animation(r, document.timeline), this.updateModalBodyHeight(), this.setupMutationObserver(), this.checkForFooterContent();
  }
  updated(t) {
    super.updated(t);
  }
  handleSlottedContentChange() {
    this.refreshBody();
  }
  setupMutationObserver() {
    this.observer = new MutationObserver((t) => {
      for (let e of t)
        if (e.type === "childList" || e.type === "characterData") {
          this.handleSlottedContentChange();
          break;
        }
    }), this.updateComplete.then(() => {
      const e = this.shadowRoot?.querySelector("#scroll-container")?.querySelector("slot");
      e && this.observeSlot(e);
    });
  }
  observeSlot(t) {
    t.removeEventListener("slotchange", this.handleSlotChange), t.addEventListener("slotchange", this.handleSlotChange), this.handleSlotChange({
      target: t,
      type: "slotchange",
      bubbles: !1,
      cancelable: !1
    });
  }
  observeNode(t) {
    if (this.nodeObservers.has(t))
      return;
    const e = new MutationObserver(
      () => this.handleSlottedContentChange()
    );
    this.nodeObservers.set(t, e), e.observe(t, {
      childList: !0,
      subtree: !0,
      characterData: !0
    });
  }
  unobserveNode(t) {
    const e = this.nodeObservers.get(t);
    e && (e.disconnect(), this.nodeObservers.delete(t));
  }
  disconnectMutationObserver() {
    const t = this.shadowRoot?.querySelector("slot");
    t && t.removeEventListener("slotchange", this.handleSlotChange), this.observedNodes.forEach((e) => this.unobserveNode(e)), this.observedNodes = [], this.observer && (this.observer.disconnect(), this.observer = null);
  }
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  }
  calculateMaxHeight() {
    const t = window.matchMedia(`(${bt()})`).matches;
    if (window.matchMedia(`(${ut()})`).matches && this.fullScreen) {
      const n = this.getFooterHeight ? 18 : 0;
      return `calc(100vh - ${this.getHeaderHeight()}px - ${this.getFooterHeight}px - ${n}px)`;
    }
    return this.needsLargerHeight = window.matchMedia(
      `(${bt()}) and (max-height: 700px)`
    ).matches, `calc(100vh - ${t ? this.calculateLargeScreenModalHeight() : this.calculateModalHeight()}px)`;
  }
  calculateModalHeight() {
    const t = window.matchMedia(`(${ut()})`).matches, e = 42, r = t && this.fullScreen ? 0 : 90, n = this.isIOS() ? 90 : 0;
    return e + r + n + this.getHeaderHeight();
  }
  calculateLargeScreenModalHeight() {
    return 60 + (this.needsLargerHeight ? 96 : 240) + this.getHeaderHeight();
  }
  getHeaderHeight() {
    return this.modalHeaderRef.value.offsetHeight;
  }
  get getFooterHeight() {
    return this.modalFooterRef.value.offsetHeight;
  }
  refreshBody() {
    this.updateModalBodyHeight();
  }
  /**
   * @description
   * checks if there is any content in the footer slot and adds padding to the modal accordingly.
   */
  checkForFooterContent() {
    this.modalFooterRef.value?.querySelector("slot")?.assignedElements()?.length ? (this.modalRef.value?.classList.add("pb-4", "m_and_up:pb-10"), this.modalFooterRef.value?.classList.add("pt-3")) : this.modalRef.value?.classList.add("pb-10", "m_and_up:pb-15");
  }
  get eventPrefix() {
    return this.prefix ? `${this.prefix}-` : "";
  }
  dispatchCloseEvent() {
    const t = new CustomEvent(`${this.eventPrefix}modal:close`, {
      bubbles: !0,
      composed: !0
    });
    this.dispatchEvent(t);
  }
  show() {
    document.body.inert = !0, this.open = !0, this.modalRef.value.showModal(), this.modalRef.value.ariaModal = "true", this.refreshBody(), window.matchMedia("(prefers-reduced-motion: reduce)").matches || this.showModalAnimation.play(), setTimeout(() => {
      document.addEventListener("click", this.handleOutsideClick);
    }, 0);
  }
  hide() {
    this.modalRef.value?.close();
  }
  onClose() {
    document.body.inert = !1, this.open = !1, this.modalRef.value.removeAttribute("aria-modal"), window.matchMedia("(prefers-reduced-motion: reduce)").matches || this.hideModalAnimation.play(), document.removeEventListener("click", this.handleOutsideClick), this.dispatchCloseEvent();
  }
  static get styles() {
    return _t`${mt(oe)}`;
  }
  render() {
    const t = {
      "width-default": this.size === "default",
      "width-large": this.size === "large",
      "height-large": this.needsLargerHeight,
      "full-screen": this.fullScreen
    };
    return Nt`<dialog ${R(this.modalRef)} class="cursor-default p-0 transform overflow-hidden text-left ${Kt(
      t
    )}" role="dialog" aria-labelledby="modal-title" aria-describedby="modal-description" ?open="${this.open}"><div class="m_and_up:flex m_and_up:items-start m_and_up:flex-col" @click="${this.handleWrapperClick}"><div id="modal-header" ${R(this.modalHeaderRef)} class="pb-4 w-full relative"><div class="flex justify-between items-center m-1"><div><slot name="header-start"></slot></div><div><slot name="header-center"></slot></div><button @click="${this.hide}" type="button" class="bdk-tw-button-icon" aria-label="${this.ariaLabelClose}" size="small" variant="overlay-neutral"><span role="presentation">${re(ne)}</span></button></div><div class="px-3 m_and_up:px-10"><div class="bdk-tw-heading-m text-coal" id="modal-title"><slot name="title"></slot></div><div id="modal-description" class="sr-only"><slot name="description"></slot></div></div></div><div id="modal-body" class="w-full relative"><div><div id="scroll-container" class="px-3 m_and_up:px-10 overflow-auto" ${R(this.modalBodyRef)}><slot></slot></div></div></div><div id="modal-footer" class="relative px-3 m_and_up:px-10 w-full" ${R(this.modalFooterRef)}><slot name="footer"></slot></div></div></dialog>`;
  }
}
_([
  y({ type: String })
], $.prototype, "prefix");
_([
  y({ type: Boolean, reflect: !0 })
], $.prototype, "fullScreen");
_([
  y({ type: String, reflect: !0 })
], $.prototype, "size");
_([
  y({ type: Boolean, reflect: !0 })
], $.prototype, "open");
_([
  y({ type: String, reflect: !0 })
], $.prototype, "ariaLabelClose");
_([
  Yt()
], $.prototype, "needsLargerHeight");
export {
  $ as Modal
};
